import React from 'react';
import { motion } from 'framer-motion';
import { Buildings, Coffee, Sparkle, ArrowRight, CheckCircle } from '@phosphor-icons/react';

const CARDS = [
  {
    id: 'corporate',
    icon: Buildings,
    tagColor: '#1A4331',
    accent: 'from-[#0F2A20] via-[#1A4331] to-[#1A4331]',
    ring: 'ring-[#1A4331]/20',
    title: 'Corporate & Event Catering',
    description:
      'Freshly prepared meals for office events, meetings, workshops, team lunches and celebrations.',
    highlights: [
      'Office Events',
      'Team Lunches',
      'Workshops & Meetings',
      'Corporate Celebrations',
      '20–500 Meals',
    ],
    cta: 'Order from Menu',
    testid: 'service-card-corporate',
    action: 'scroll-menu',
  },
  {
    id: 'employee',
    icon: Coffee,
    tagColor: '#C05C3D',
    accent: 'from-[#C05C3D] via-[#B84E32] to-[#8F3D27]',
    ring: 'ring-[#C05C3D]/25',
    title: 'Daily Employee Meals',
    description:
      'Fresh breakfast and lunch catering for offices, teams and everyday employee dining.',
    highlights: [
      'Daily Breakfast',
      'Daily Lunch',
      'Breakfast + Lunch',
      'Weekly / Monthly Plans',
      'Flexible Headcount',
    ],
    cta: 'Request Meal Plan',
    testid: 'service-card-employee',
    action: 'scroll-employee',
  },
  {
    id: 'festival',
    icon: Sparkle,
    tagColor: '#FBBF24',
    accent: 'from-[#F59E0B] via-[#FBBF24] to-[#F59E0B]',
    ring: 'ring-[#FBBF24]/30',
    title: 'Festival Specials',
    description:
      'Special menus for Onam, Diwali, Pongal and other festive occasions.',
    highlights: [
      'Onam Special',
      'Diwali Special',
      'Pongal & Sankranti',
      'Regional Festival Menus',
      'Custom Festive Meals',
    ],
    cta: 'Request Festival Menu',
    testid: 'service-card-festival',
    action: 'scroll-festival',
  },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export const CateringServiceCards = () => {
  const handleClick = (action) => {
    if (action === 'scroll-menu') return scrollTo('catering-menu-section');
    if (action === 'scroll-employee') return scrollTo('employee-meal-enquiry');
    if (action === 'scroll-festival') return scrollTo('festival-enquiry');
  };

  return (
    <section
      className="relative px-6 pb-4 pt-2 md:pb-8"
      data-testid="catering-service-selection"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10"
        >
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#1A4331] mb-3">
            Three Ways to Cater
          </p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading mb-3">
            Corporate Catering for Every Occasion
          </h2>
          <p className="text-base sm:text-lg text-stone-600">
            From corporate events and daily employee meals to festive celebrations — freshly prepared food, delivered with care.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.08 }}
                className={`group relative bg-white rounded-3xl p-7 md:p-8 border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col`}
                data-testid={card.testid}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.accent} flex items-center justify-center shadow-md mb-5`}>
                  <Icon size={26} weight="duotone" className="text-white" />
                </div>

                <h3 className="text-xl font-semibold text-[#1C1917] font-heading mb-2 leading-tight">
                  {card.title}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed mb-5">
                  {card.description}
                </p>

                <ul className="space-y-2 mb-7 flex-1">
                  {card.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-sm text-stone-700">
                      <CheckCircle size={16} weight="fill" style={{ color: card.tagColor }} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleClick(card.action)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1C1917] hover:bg-[#0F2A20] text-white font-semibold text-sm px-5 py-3.5 rounded-full transition-all duration-200 group-hover:scale-[1.02] mt-auto"
                  data-testid={`${card.testid}-cta`}
                >
                  {card.cta}
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CateringServiceCards;
